import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import { navigateRequireAuth } from '../../utils/authRedirect';
import { toast } from 'react-toastify';
import styled, { keyframes } from 'styled-components';
import { designerItemsAPI } from '../../api/designerItemsAPI';
import { artistItemsAPI } from '../../api/artistItemsAPI';
import ListingTypeGuidelines from '../../components/items/ListingTypeGuidelines';
import ListingMediaUploadField from '../../components/items/ListingMediaUploadField';
import PaintingDimensionsFields from '../../components/items/PaintingDimensionsFields';
import ListingCategorySelect from '../../components/items/ListingCategorySelect';
import { formatPaintingDimensionsCm, paintingDimensionsError } from '../../utils/paintingDimensions';
import { LUXURY_ITEM_CATEGORIES } from '../../constants/preOwnedItemCategories';
import {
  EMPTY_CATEGORY_TAXONOMY,
  buildTaxonomySubcategory,
  validateCategoryTaxonomy,
} from '../../constants/listingCategoryTaxonomy';
import {
  countShopImageFiles,
  isShopVideoFile,
} from '../../utils/shopMedia';
import SEO from '../../components/seo/SEO';
import {
  trackShopCreateStarted,
  trackShopCreateSucceeded,
  trackShopCreateFailed,
} from '../../utils/analytics';
import { requestIdFromError } from '../../utils/requestId';
import { uploadShopCreateCatalog } from '../../utils/uploadShopCreateCatalog';
import {
  isAlreadyHaveShopError,
  normalizeShopApprovalStatus,
} from '../../utils/shopOwnerPortal';

const STOCK_OPTIONS = Array.from({ length: 20 }, (_, i) => String(i + 1));

const EMPTY_CATALOG_FORM = {
  name: '',
  price: '',
  description: '',
  painting_length: '',
  painting_width: '',
  fragile: false,
  weight_estimate_band: 'under_2kg',
  in_stock: '1',
  ...EMPTY_CATEGORY_TAXONOMY,
};

/* ─── Animations ───────────────────────────────────────────────────── */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; }`;

/* ─── Layout ────────────────────────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 1rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 680px;
  background: white;
  border-radius: 1.25rem;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease;
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem 2rem 1.5rem;
  color: white;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 800;
  margin: 0 0 0.35rem;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  opacity: 0.85;
  margin: 0;
`;

/* ─── Progress bar ─────────────────────────────────────────────────── */
const ProgressWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const StepCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  transition: all 0.3s;
  background: ${p => p.$done ? '#10b981' : p.$active ? '#764ba2' : '#e5e7eb'};
  color: ${p => p.$done || p.$active ? 'white' : '#9ca3af'};
  flex-shrink: 0;
`;

const StepLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${p => p.$active ? '#764ba2' : '#9ca3af'};
  margin-top: 0.35rem;
  white-space: nowrap;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
`;

const Connector = styled.div`
  height: 2px;
  width: 48px;
  background: ${p => p.$done ? '#10b981' : '#e5e7eb'};
  flex-shrink: 0;
  margin-bottom: 1.3rem;
  transition: background 0.3s;
`;

/* ─── Form area ────────────────────────────────────────────────────── */
const FormArea = styled.div`
  padding: 2rem;
`;

const StepTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.35rem;
`;

const StepDesc = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 1.5rem;
`;

const Group = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const Required = styled.span`color: #ef4444; margin-left: 2px;`;
const Optional = styled.span`color: #9ca3af; font-weight: 400; font-size: 0.8rem; margin-left: 4px;`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1.5px solid ${p => p.$error ? '#ef4444' : '#d1d5db'};
  border-radius: 0.5rem;
  font-size: 0.95rem;
  transition: border-color 0.2s;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #764ba2; }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1.5px solid ${p => p.$error ? '#ef4444' : '#d1d5db'};
  border-radius: 0.5rem;
  font-size: 0.95rem;
  background: #fff;
  color: #1e293b;
  transition: border-color 0.2s;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #764ba2; }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  line-height: 1.4;
  input {
    margin-top: 0.15rem;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    cursor: pointer;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1.5px solid ${p => p.$error ? '#ef4444' : '#d1d5db'};
  border-radius: 0.5rem;
  font-size: 0.95rem;
  resize: vertical;
  min-height: ${p => p.$tall ? '130px' : '100px'};
  transition: border-color 0.2s;
  box-sizing: border-box;
  font-family: inherit;
  &:focus { outline: none; border-color: #764ba2; }
`;

const ErrorMsg = styled.p`
  font-size: 0.8rem;
  color: #ef4444;
  margin: 0.3rem 0 0;
`;

const CharCount = styled.p`
  font-size: 0.75rem;
  color: ${p => p.$over ? '#ef4444' : '#9ca3af'};
  margin: 0.25rem 0 0;
  text-align: right;
`;

/* ─── Logo upload ──────────────────────────────────────────────────── */
const LogoUploadArea = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
`;

const LogoPreview = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 2px dashed ${p => p.$hasImage ? '#764ba2' : '#d1d5db'};
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  cursor: pointer;
  transition: border-color 0.2s;
  &:hover { border-color: #764ba2; }
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const LogoUploadBtn = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.1rem;
  background: #f3f0ff;
  color: #764ba2;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #e9e3ff; }
`;

/* ─── Navigation buttons ───────────────────────────────────────────── */
const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 2rem;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const BtnBack = styled.button`
  padding: 0.7rem 1.5rem;
  border: 1.5px solid #d1d5db;
  border-radius: 0.5rem;
  background: white;
  color: #374151;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #764ba2; color: #764ba2; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const BtnNext = styled.button`
  padding: 0.7rem 1.75rem;
  border: none;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* ─── Review step ──────────────────────────────────────────────────── */
const ReviewBox = styled.div`
  background: #f8f9fa;
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

const ReviewLabel = styled.p`
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #9ca3af;
  margin: 0 0 0.3rem;
  letter-spacing: 0.05em;
`;

const ReviewValue = styled.p`
  font-size: 0.95rem;
  color: #1e293b;
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.5;
`;

const StatusNote = styled.div`
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 0.5rem;
  padding: 0.9rem 1rem;
  font-size: 0.875rem;
  color: #92400e;
  margin-top: 1rem;
`;

/* ─── Catalog step ─────────────────────────────────────────────────── */
const CatalogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin: 0 0 1rem;
`;

const CatalogCard = styled.div`
  background: #f8f9fa;
  border-radius: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  position: relative;
`;

const CatalogCardImg = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
`;

const CatalogCardVideo = styled.video`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  background: #0f172a;
`;

const CatalogCardName = styled.p`
  font-weight: 600;
  font-size: 0.9rem;
  color: #1e293b;
  margin: 0 0 0.25rem;
`;

const CatalogCardPrice = styled.p`
  font-size: 0.85rem;
  color: #10b981;
  font-weight: 700;
  margin: 0;
`;

const RemoveCatalogBtn = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #ef4444; }
`;

const AddItemForm = styled.div`
  background: #f0fdf4;
  border: 1px dashed #a7f3d0;
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const CatalogFormTitle = styled.p`
  font-weight: 700;
  font-size: 0.95rem;
  color: #065f46;
  margin: 0 0 0.35rem;
`;

const CatalogFormHint = styled.p`
  font-size: 0.85rem;
  color: #047857;
  margin: 0 0 1rem;
  line-height: 1.45;
`;

const CatalogSavedBanner = styled.div`
  background: #ecfdf5;
  border: 1px solid #6ee7b7;
  border-radius: 0.75rem;
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;
  color: #065f46;
  font-size: 0.9rem;
  line-height: 1.45;
  font-weight: 600;
`;

const BtnSaveItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 0.5rem;
  background: #10b981;
  color: white;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  &:hover { background: #059669; }
`;

const BtnAddAnother = styled.button`
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.7rem 1rem;
  border: 1.5px dashed #10b981;
  border-radius: 0.5rem;
  background: white;
  color: #047857;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  &:hover { background: #f0fdf4; }
`;

/* ─── Component ────────────────────────────────────────────────────── */
const STEPS = [
  { label: 'Brand', icon: '🏪' },
  { label: 'Policies', icon: '📋' },
  { label: 'Catalog', icon: '📦' },
  { label: 'Review', icon: '✅' },
];

const CreateShopPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isArtistGallery = searchParams.get('kind') === 'artists';
  const shopAPI = isArtistGallery ? artistItemsAPI : designerItemsAPI;
  const shopType = isArtistGallery ? 'artist' : 'designer';
  const { isAuthenticated } = useSelector(s => s.auth);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [finishingUploads, setFinishingUploads] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);

  const [form, setForm] = useState({
    brand_name: '',
    bio: '',
    instagram_handle: '',
    social_link: '',
    return_policy: '',
    shipping_policy: '',
    accepts_customizations: null,
    customization_price_mode: '',
    customization_min_price: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [catalogItems, setCatalogItems] = useState([]);
  const catalogItemsRef = useRef([]);
  const [catalogForm, setCatalogForm] = useState({ ...EMPTY_CATALOG_FORM });
  const [catalogErrors, setCatalogErrors] = useState({});
  const [catalogMedia, setCatalogMedia] = useState([]);
  const [showCatalogForm, setShowCatalogForm] = useState(true);
  const catalogMediaUploadApiRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) { navigateRequireAuth(navigate, location); return; }
    if (location.state?.reapply) {
      setCheckingExisting(false);
      return;
    }
    // Check if user already has a shop
    (async () => {
      try {
        const shop = await shopAPI.getMyShop({ compact: true });
        const approval = normalizeShopApprovalStatus(shop?.approval_status);
        if (shop && shop.id && approval === 'APPROVED') {
          const approvedPath = isArtistGallery
            ? `/artist-shop/${shopAPI.buildShopRef(shop)}`
            : `/designer-shop/${shopAPI.buildShopRef(shop)}`;
          navigate(approvedPath, { replace: true });
          return;
        }
        // Rejected shops can re-apply via this create flow.
        if (shop && shop.id && approval && approval !== 'REJECTED') {
          navigate(`/shop-pending?type=${shopType}`, { replace: true });
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          toast.error('Your session expired. Please sign in again before creating your shop.');
          navigateRequireAuth(navigate, location);
          return;
        }
        // 404 = no shop yet, which is expected
      } finally {
        setCheckingExisting(false);
      }
    })();
  }, [isAuthenticated, navigate, location, shopAPI, shopType, isArtistGallery]);

  const update = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
  };

  const handleLogoChange = (e) => {
    const MAX_FILE_SIZE = 10 * 1024 * 1024;
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`"${file.name}" has an unsupported format. Use JPEG, PNG, GIF, or WebP.`);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`"${file.name}" is too large (max 10MB).`);
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const validateStep0 = () => {
    const errs = {};
    if (!form.brand_name.trim()) errs.brand_name = 'Brand name is required.';
    else if (form.brand_name.trim().length < 2) errs.brand_name = 'Brand name must be at least 2 characters.';
    if (form.bio.length > 600) errs.bio = 'Bio must be under 600 characters.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep1 = () => {
    const errs = {};
    if (form.return_policy.trim() && form.return_policy.length > 1000)
      errs.return_policy = 'Return policy must be under 1000 characters.';
    if (form.shipping_policy.trim() && form.shipping_policy.length > 1000)
      errs.shipping_policy = 'Shipping policy must be under 1000 characters.';
    if (isArtistGallery && form.accepts_customizations === null)
      errs.accepts_customizations = 'Choose whether you accept customization requests.';
    if (isArtistGallery && form.accepts_customizations && !form.customization_price_mode)
      errs.customization_price_mode = 'Choose how your starting price should appear.';
    if (
      isArtistGallery
      && form.accepts_customizations
      && form.customization_price_mode === 'minimum'
      && (!Number.isFinite(Number(form.customization_min_price)) || Number(form.customization_min_price) <= 0)
    ) errs.customization_min_price = 'Enter a minimum customization price greater than 0.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCatalogTaxonomyChange = (patch) => {
    setCatalogForm((f) => ({ ...f, ...patch }));
    setCatalogErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch || {}).forEach((key) => { delete next[key]; });
      return next;
    });
  };

  const catalogDraftStarted = () => Boolean(
    catalogForm.name.trim()
    || catalogForm.price
    || catalogForm.description.trim()
    || catalogForm.category
    || catalogForm.painting_length
    || catalogForm.painting_width
    || catalogForm.fragile
    || catalogMedia.length > 0
  );

  const commitCatalogItem = async () => {
    if (!catalogForm.name.trim()) {
      toast.error('Item name is required.');
      return false;
    }
    if (!catalogForm.price || Number(catalogForm.price) <= 0) {
      toast.error('Price must be greater than 0.');
      return false;
    }
    if (catalogMedia.length === 0) {
      toast.error('Add at least one photo or video for this item.');
      return false;
    }
    if (countShopImageFiles(catalogMedia) < 1) {
      toast.error('Include at least one photo so the item can show a thumbnail. Videos are optional extras.');
      return false;
    }
    let resolvedSubcategory = '';
    if (!isArtistGallery) {
      const taxonomyErrors = validateCategoryTaxonomy(catalogForm);
      if (Object.keys(taxonomyErrors).length > 0) {
        setCatalogErrors(taxonomyErrors);
        toast.error(Object.values(taxonomyErrors)[0]);
        return false;
      }
      resolvedSubcategory = buildTaxonomySubcategory(catalogForm);
    }
    if (isArtistGallery) {
      const dimError = paintingDimensionsError(catalogForm.painting_length, catalogForm.painting_width);
      if (dimError) {
        toast.error(dimError);
        return false;
      }
    }
    setFinishingUploads(true);
    try {
      const outcomes = catalogMediaUploadApiRef.current
        ? await catalogMediaUploadApiRef.current.waitForUploads()
        : catalogMedia.map(() => null);
      const failedCount = outcomes.filter((o) => !o || (!o.key && !o.deferred)).length;
      if (failedCount > 0) {
        toast.error(
          `${failedCount} photo/video failed to upload. Tap Retry on the failed thumbnail, then try again.`,
        );
        return false;
      }
      const nextItem = {
        name: catalogForm.name.trim(),
        price: catalogForm.price,
        description: catalogForm.description.trim(),
        category: isArtistGallery ? (catalogForm.category || 'Arts & Crafts') : catalogForm.category,
        subcategory: resolvedSubcategory || undefined,
        size: isArtistGallery
          ? formatPaintingDimensionsCm(catalogForm.painting_length, catalogForm.painting_width)
          : undefined,
        fragile: Boolean(catalogForm.fragile),
        weight_estimate_band: catalogForm.weight_estimate_band || 'under_2kg',
        in_stock: catalogForm.in_stock || '1',
        images: catalogMedia.map((m, i) => ({
          file: m.file,
          ...(outcomes[i]?.key ? { pendingKey: outcomes[i].key } : {}),
        })),
        previews: catalogMedia.map((m) => m.preview),
      };
      const next = [...catalogItemsRef.current, nextItem];
      catalogItemsRef.current = next;
      setCatalogItems(next);
      setCatalogForm({ ...EMPTY_CATALOG_FORM });
      setCatalogErrors({});
      setCatalogMedia([]);
      setShowCatalogForm(false);
      return next;
    } finally {
      setFinishingUploads(false);
    }
  };

  const addCatalogItem = async () => {
    if (!await commitCatalogItem()) return;
    toast.success('Item saved. Add another, or tap Next to continue.');
  };

  const removeCatalogItem = (index) => {
    const next = catalogItemsRef.current.filter((_, i) => i !== index);
    catalogItemsRef.current = next;
    setCatalogItems(next);
    if (next.length <= 0) setShowCatalogForm(true);
  };

  const resolveCatalogItems = () => {
    if (catalogDraftStarted()) return commitCatalogItem();
    return catalogItemsRef.current;
  };

  const handleNext = async () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    if (step === 2) {
      const items = await resolveCatalogItems();
      if (!items) return;
      if (items.length === 0) {
        toast.error('Fill in your first item, then tap Save & Continue.');
        setShowCatalogForm(true);
        return;
      }
    }
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    const items = await resolveCatalogItems();
    if (!items) {
      setStep(2);
      setShowCatalogForm(true);
      return;
    }
    if (items.length === 0) {
      toast.error('Add at least one catalog item before submitting.');
      setStep(2);
      setShowCatalogForm(true);
      return;
    }
    setSubmitting(true);
    const analyticsBase = {
      shop_type: shopType,
      catalog_item_count: items.length,
      has_logo: Boolean(logoFile),
    };
    trackShopCreateStarted(analyticsBase);
    let stage = 'shop';
    let shopAlreadyExisted = false;
    let shopId;
    try {
      // Create shop + catalog together so admin can review each item.
      const fd = new FormData();
      fd.append('brand_name', form.brand_name.trim());
      fd.append('bio', form.bio.trim());
      fd.append('instagram_handle', form.instagram_handle.trim());
      fd.append('social_link', form.social_link.trim());
      fd.append('return_policy', form.return_policy.trim());
      fd.append('shipping_policy', form.shipping_policy.trim());
      if (isArtistGallery) {
        fd.append('accepts_customizations', form.accepts_customizations ? 'true' : 'false');
        if (form.accepts_customizations && form.customization_price_mode === 'discussed') {
          fd.append('pricing_to_be_discussed', 'true');
        }
        if (form.accepts_customizations && form.customization_price_mode === 'minimum') {
          fd.append(
            'customization_min_price_cents',
            String(Math.round(Number(form.customization_min_price) * 100)),
          );
        }
      }
      if (logoFile) fd.append('logo', logoFile);
      try {
        const created = await shopAPI.createShop(fd);
        shopId = created?.id;
      } catch (shopErr) {
        const msg = String(shopErr?.response?.data?.error || shopErr?.message || '');
        if (!isAlreadyHaveShopError(msg)) {
          throw shopErr;
        }
        shopAlreadyExisted = true;
        try {
          const existing = await shopAPI.getMyShop();
          const approval = normalizeShopApprovalStatus(existing?.approval_status);
          if (approval === 'APPROVED' && existing?.id) {
            const approvedPath = isArtistGallery
              ? `/artist-shop/${shopAPI.buildShopRef(existing)}`
              : `/designer-shop/${shopAPI.buildShopRef(existing)}`;
            toast.success('Your shop is already approved.');
            trackShopCreateSucceeded({
              ...analyticsBase,
              shop_id: existing.id,
              shop_already_existed: true,
            });
            navigate(approvedPath, { replace: true });
            return;
          }
          if (approval === 'REJECTED') {
            toast.error('This shop was not approved. Update your details and submit again.');
            return;
          }
        } catch {
          // Pending shops still need catalog upload below.
        }
      }
      stage = 'catalog';
      await uploadShopCreateCatalog({
        kind: shopType,
        brandName: form.brand_name.trim(),
        items,
        api: isArtistGallery
          ? {
              createDraft: artistItemsAPI.createArtistItemDraft,
              uploadMedia: artistItemsAPI.uploadArtistItemMedia,
              finalize: artistItemsAPI.finalizeArtistItemDraft,
              initDirectUpload: artistItemsAPI.initArtistDirectUpload,
              signDirectParts: artistItemsAPI.signArtistDirectParts,
              completeDirectUpload: artistItemsAPI.completeArtistDirectUpload,
              abortDirectUpload: artistItemsAPI.abortArtistDirectUpload,
              attachPending: artistItemsAPI.attachArtistPendingImages,
            }
          : {
              createDraft: designerItemsAPI.createDesignerItemDraft,
              uploadMedia: designerItemsAPI.uploadDesignerItemMedia,
              finalize: designerItemsAPI.finalizeDesignerItemDraft,
              initDirectUpload: designerItemsAPI.initDesignerDirectUpload,
              signDirectParts: designerItemsAPI.signDesignerDirectParts,
              completeDirectUpload: designerItemsAPI.completeDesignerDirectUpload,
              abortDirectUpload: designerItemsAPI.abortDesignerDirectUpload,
              attachPending: designerItemsAPI.attachDesignerPendingImages,
            },
      });

      toast.success('Your shop has been submitted for review!');
      trackShopCreateSucceeded({
        ...analyticsBase,
        shop_id: shopId,
        shop_already_existed: shopAlreadyExisted,
      });
      navigate(`/shop-pending?type=${shopType}`, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      trackShopCreateFailed({
        ...analyticsBase,
        shop_id: shopId,
        shop_already_existed: shopAlreadyExisted,
        stage,
        error: err?.response?.data?.error || err?.message || 'Failed to create shop',
        status: status ?? null,
        request_id: requestIdFromError(err),
      });
      if (status === 401 || status === 403) {
        toast.error('Your session expired. Please sign in again, then submit your shop.');
        navigateRequireAuth(navigate, location);
      } else {
        const msg = err.response?.data?.error || err?.message || 'Failed to create shop. Please try again.';
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingExisting) {
    return (
      <Page>
        <SEO
          title={isArtistGallery ? 'Create Gallery | Flea Marketplace' : 'Create Shop | Flea Marketplace'}
          description={
            isArtistGallery
              ? 'Create your artist gallery on Flea Marketplace.'
              : 'Create your designer shop on Flea Marketplace.'
          }
          noindex
        />
        <div style={{ color: 'white', fontSize: '1.1rem', marginTop: '4rem' }}>Loading…</div>
      </Page>
    );
  }

  return (
    <Page>
      <SEO
        title={isArtistGallery ? 'Create Gallery | Flea Marketplace' : 'Create Shop | Flea Marketplace'}
        description={
          isArtistGallery
            ? 'Create your artist gallery on Flea Marketplace.'
            : 'Create your designer shop on Flea Marketplace.'
        }
        noindex
      />
      <Card>
        <CardHeader>
          <Title>{isArtistGallery ? 'Create Your Gallery' : 'Create Your Shop'}</Title>
          <Subtitle>
            {isArtistGallery
              ? 'Share original art and handmade work with Flea buyers'
              : 'Build your brand on Flea and start selling to thousands of buyers'}
          </Subtitle>
        </CardHeader>

        <ListingTypeGuidelines type="designer" />

        {/* Progress indicator */}
        <ProgressWrap>
          {STEPS.map((s, i) => (
            <React.Fragment key={i}>
              <StepItem>
                <StepCircle $active={step === i} $done={step > i}>
                  {step > i ? '✓' : i + 1}
                </StepCircle>
                <StepLabel $active={step === i}>{s.label}</StepLabel>
              </StepItem>
              {i < STEPS.length - 1 && <Connector $done={step > i} />}
            </React.Fragment>
          ))}
        </ProgressWrap>

        {/* Step 0: Brand identity */}
        {step === 0 && (
          <FormArea>
            <StepTitle>Brand Identity</StepTitle>
            <StepDesc>Tell buyers who you are. Your brand name and logo are the first things they see.</StepDesc>

            <Group>
              <Label>Brand Name <Required>*</Required></Label>
              <Input
                type="text"
                placeholder="e.g. Nour's Nails Studio"
                value={form.brand_name}
                onChange={e => update('brand_name', e.target.value)}
                maxLength={150}
                $error={!!errors.brand_name}
              />
              {errors.brand_name && <ErrorMsg>{errors.brand_name}</ErrorMsg>}
            </Group>

            <Group>
              <Label>Shop Logo <Optional>(optional)</Optional></Label>
              <LogoUploadArea>
                <LogoPreview $hasImage={!!logoPreview}>
                  {logoPreview
                    ? <img src={logoPreview} alt="logo preview" />
                    : <span style={{ fontSize: '2rem' }}>🏪</span>}
                </LogoPreview>
                <div>
                  <LogoUploadBtn htmlFor="logo-input">
                    📷 {logoFile ? 'Change logo' : 'Upload logo'}
                    <input
                      id="logo-input"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleLogoChange}
                    />
                  </LogoUploadBtn>
                  <p style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '0.4rem' }}>
                    Square image, max 5 MB. PNG or JPEG recommended.
                  </p>
                </div>
              </LogoUploadArea>
            </Group>

            <Group>
              <Label>Brand Bio <Optional>(optional)</Optional></Label>
              <Textarea
                placeholder="Tell your story — what makes your brand unique, your inspiration, your craft…"
                value={form.bio}
                onChange={e => update('bio', e.target.value)}
                maxLength={600}
                $error={!!errors.bio}
              />
              <CharCount $over={form.bio.length > 600}>{form.bio.length}/600</CharCount>
              {errors.bio && <ErrorMsg>{errors.bio}</ErrorMsg>}
            </Group>

            <Group>
              <Label>Instagram Handle <Optional>(optional)</Optional></Label>
              <Input
                type="text"
                placeholder="@yourbrand"
                value={form.instagram_handle}
                onChange={e => update('instagram_handle', e.target.value)}
                maxLength={100}
              />
              <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>
                Shown on your shop page and included in the admin review.
              </p>
            </Group>

            <Group>
              <Label>Other Social / Website <Optional>(optional)</Optional></Label>
              <Input
                type="url"
                placeholder="https://tiktok.com/@yourbrand or https://yourbrand.com"
                value={form.social_link}
                onChange={e => update('social_link', e.target.value)}
                maxLength={500}
              />
            </Group>
          </FormArea>
        )}

        {/* Step 1: Policies */}
        {step === 1 && (
          <FormArea>
            <StepTitle>Shop Policies</StepTitle>
            <StepDesc>Clear policies build trust with buyers. You can always update these later.</StepDesc>

            <Group>
              <Label>Return Policy <Optional>(optional)</Optional></Label>
              <Textarea
                $tall
                placeholder="e.g. Items can be returned within 7 days of delivery if unused and in original condition…"
                value={form.return_policy}
                onChange={e => update('return_policy', e.target.value)}
                maxLength={1000}
                $error={!!errors.return_policy}
              />
              <CharCount $over={form.return_policy.length > 1000}>{form.return_policy.length}/1000</CharCount>
              {errors.return_policy && <ErrorMsg>{errors.return_policy}</ErrorMsg>}
            </Group>

            <Group>
              <Label>Shipping Policy <Optional>(optional)</Optional></Label>
              <Textarea
                $tall
                placeholder="e.g. Orders ship within 3–5 business days. Nationwide delivery available via our courier partners…"
                value={form.shipping_policy}
                onChange={e => update('shipping_policy', e.target.value)}
                maxLength={1000}
                $error={!!errors.shipping_policy}
              />
              <CharCount $over={form.shipping_policy.length > 1000}>{form.shipping_policy.length}/1000</CharCount>
              {errors.shipping_policy && <ErrorMsg>{errors.shipping_policy}</ErrorMsg>}
            </Group>

            {isArtistGallery && (
              <Group>
                <Label>Do you accept customization requests, or do you sell only ready-made items? <Required>*</Required></Label>
                <CheckboxLabel>
                  <input
                    type="radio"
                    name="accepts-customizations"
                    checked={form.accepts_customizations === true}
                    onChange={() => update('accepts_customizations', true)}
                  />
                  <span>I accept customization requests</span>
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="radio"
                    name="accepts-customizations"
                    checked={form.accepts_customizations === false}
                    onChange={() => setForm((current) => ({
                      ...current,
                      accepts_customizations: false,
                      customization_price_mode: '',
                      customization_min_price: '',
                    }))}
                  />
                  <span>I sell only ready-made items</span>
                </CheckboxLabel>
                {errors.accepts_customizations && <ErrorMsg>{errors.accepts_customizations}</ErrorMsg>}

                {form.accepts_customizations && (
                  <div style={{ marginTop: '1rem' }}>
                    <Label>How should your starting price appear? <Required>*</Required></Label>
                    <CheckboxLabel>
                      <input
                        type="radio"
                        name="customization-price-mode"
                        checked={form.customization_price_mode === 'minimum'}
                        onChange={() => update('customization_price_mode', 'minimum')}
                      />
                      <span>Set a minimum customization price</span>
                    </CheckboxLabel>
                    <CheckboxLabel>
                      <input
                        type="radio"
                        name="customization-price-mode"
                        checked={form.customization_price_mode === 'discussed'}
                        onChange={() => update('customization_price_mode', 'discussed')}
                      />
                      <span>To be discussed</span>
                    </CheckboxLabel>
                    {errors.customization_price_mode && <ErrorMsg>{errors.customization_price_mode}</ErrorMsg>}
                    {form.customization_price_mode === 'minimum' ? (
                      <div style={{ marginTop: '0.75rem' }}>
                        <Label>Minimum customization price <Required>*</Required></Label>
                        <Input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={form.customization_min_price}
                          onChange={(event) => update('customization_min_price', event.target.value)}
                          $error={!!errors.customization_min_price}
                          placeholder="Amount in EGP"
                        />
                        {errors.customization_min_price && <ErrorMsg>{errors.customization_min_price}</ErrorMsg>}
                        <p style={{ fontSize: '0.78rem', color: '#64748b', lineHeight: 1.45 }}>
                          This is a guide for buyers, not a fixed price. You can send a lower or higher quote for a specific request.
                        </p>
                      </div>
                    ) : form.customization_price_mode === 'discussed' ? (
                      <p style={{ fontSize: '0.78rem', color: '#64748b' }}>Buyers will see ‘To be discussed’.</p>
                    ) : null}
                  </div>
                )}
              </Group>
            )}
          </FormArea>
        )}

        {/* Step 2: Catalog */}
        {step === 2 && (
          <FormArea>
            <StepTitle>Your Catalog</StepTitle>
            <StepDesc>
              Fill in your first item, then tap Save &amp; Continue. You can add more items after saving.
            </StepDesc>

            {catalogItems.length > 0 && (
              <CatalogSavedBanner>
                {catalogItems.length} item{catalogItems.length !== 1 ? 's' : ''} saved. Tap Next to continue, or add another item.
              </CatalogSavedBanner>
            )}

            {catalogItems.length > 0 && (
              <CatalogGrid>
                {catalogItems.map((item, i) => {
                  const firstIsVideo = isShopVideoFile(item.images?.[0]?.file || item.images?.[0]);
                  return (
                  <CatalogCard key={i}>
                    <RemoveCatalogBtn onClick={() => removeCatalogItem(i)}>✕</RemoveCatalogBtn>
                    {firstIsVideo ? (
                      <CatalogCardVideo src={item.previews[0]} muted preload="metadata" />
                    ) : (
                      <CatalogCardImg src={item.previews[0]} alt={item.name} />
                    )}
                    <CatalogCardName>{item.name}</CatalogCardName>
                    <CatalogCardPrice>{Number(item.price).toLocaleString()} EGP</CatalogCardPrice>
                  </CatalogCard>
                  );
                })}
              </CatalogGrid>
            )}

            {showCatalogForm ? (
            <AddItemForm>
              <CatalogFormTitle>Item {catalogItems.length + 1}</CatalogFormTitle>
              <CatalogFormHint>
                Save this item to your catalog. After that you can add more or continue to review.
              </CatalogFormHint>
              <Group>
                <Label>Item Name <Required>*</Required></Label>
                <Input
                  type="text"
                  placeholder="e.g. Handmade Tote Bag"
                  value={catalogForm.name}
                  onChange={e => setCatalogForm(f => ({ ...f, name: e.target.value }))}
                  maxLength={150}
                />
              </Group>
              {isArtistGallery ? (
                <PaintingDimensionsFields
                  length={catalogForm.painting_length}
                  width={catalogForm.painting_width}
                  onLengthChange={(v) => setCatalogForm((f) => ({ ...f, painting_length: v }))}
                  onWidthChange={(v) => setCatalogForm((f) => ({ ...f, painting_width: v }))}
                />
              ) : null}
              {!isArtistGallery ? (
                <ListingCategorySelect
                  value={catalogForm}
                  onChange={handleCatalogTaxonomyChange}
                  errors={catalogErrors}
                  categories={LUXURY_ITEM_CATEGORIES}
                  ui={{ FormGroup: Group, Label, Select, ErrorText: ErrorMsg }}
                />
              ) : null}
              <Group>
                <Label>Price (EGP) <Required>*</Required></Label>
                <Input
                  type="number"
                  placeholder="e.g. 350"
                  value={catalogForm.price}
                  onChange={e => setCatalogForm(f => ({ ...f, price: e.target.value }))}
                  min="1"
                />
              </Group>
              <Group>
                <Label>Description <Optional>(optional)</Optional></Label>
                <Textarea
                  placeholder="Brief description of this item…"
                  value={catalogForm.description}
                  onChange={e => setCatalogForm(f => ({ ...f, description: e.target.value }))}
                  maxLength={500}
                />
              </Group>
              <Group>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={catalogForm.fragile}
                    onChange={e => setCatalogForm(f => ({ ...f, fragile: e.target.checked }))}
                  />
                  <span>This item is fragile or prone to breaking (needs careful packing/handling)</span>
                </CheckboxLabel>
              </Group>
              <Group>
                <Label>Stock <Required>*</Required></Label>
                <Select
                  value={catalogForm.in_stock}
                  onChange={e => setCatalogForm(f => ({ ...f, in_stock: e.target.value }))}
                >
                  {STOCK_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Select>
              </Group>
              <Group>
                <ListingMediaUploadField
                  mediaItems={catalogMedia}
                  onChange={setCatalogMedia}
                  maxMedia={10}
                  minMedia={1}
                  minPhotos={1}
                  minRecommendedPhotos={3}
                  idPrefix="shop-catalog"
                  label="Photos & videos"
                  pickTimeUpload
                  uploadApiRef={catalogMediaUploadApiRef}
                />
              </Group>
              <BtnSaveItem type="button" onClick={addCatalogItem} disabled={finishingUploads}>
                {finishingUploads ? 'Finishing photo uploads…' : 'Add another item'}
              </BtnSaveItem>
            </AddItemForm>
            ) : (
              <BtnAddAnother type="button" onClick={() => setShowCatalogForm(true)}>
                + Add another item
              </BtnAddAnother>
            )}
          </FormArea>
        )}

        {/* Step 3: Review & submit */}
        {step === 3 && (
          <FormArea>
            <StepTitle>Review Your Shop</StepTitle>
            <StepDesc>Everything look good? You can edit your shop anytime after it's approved.</StepDesc>

            <ReviewBox>
              <ReviewLabel>Brand Name</ReviewLabel>
              <ReviewValue>{form.brand_name || '—'}</ReviewValue>
            </ReviewBox>

            {logoPreview && (
              <ReviewBox style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <img src={logoPreview} alt="logo" style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                <div>
                  <ReviewLabel style={{ margin: 0 }}>Logo</ReviewLabel>
                  <ReviewValue style={{ fontSize: '0.85rem' }}>{logoFile?.name}</ReviewValue>
                </div>
              </ReviewBox>
            )}

            {form.bio && (
              <ReviewBox>
                <ReviewLabel>Bio</ReviewLabel>
                <ReviewValue>{form.bio}</ReviewValue>
              </ReviewBox>
            )}

            {form.instagram_handle && (
              <ReviewBox>
                <ReviewLabel>Instagram</ReviewLabel>
                <ReviewValue>{form.instagram_handle}</ReviewValue>
              </ReviewBox>
            )}

            {form.social_link && (
              <ReviewBox>
                <ReviewLabel>Social / Website</ReviewLabel>
                <ReviewValue>{form.social_link}</ReviewValue>
              </ReviewBox>
            )}

            {form.return_policy && (
              <ReviewBox>
                <ReviewLabel>Return Policy</ReviewLabel>
                <ReviewValue>{form.return_policy}</ReviewValue>
              </ReviewBox>
            )}

            {form.shipping_policy && (
              <ReviewBox>
                <ReviewLabel>Shipping Policy</ReviewLabel>
                <ReviewValue>{form.shipping_policy}</ReviewValue>
              </ReviewBox>
            )}

            {isArtistGallery && (
              <ReviewBox>
                <ReviewLabel>Customization</ReviewLabel>
                <ReviewValue>
                  {form.accepts_customizations ? 'Accepts customization requests' : 'Ready-made items only'}
                </ReviewValue>
                {form.accepts_customizations && (
                  <ReviewValue style={{ marginTop: '0.4rem', fontSize: '0.85rem' }}>
                    {form.customization_price_mode === 'minimum'
                      ? `Minimum customization price: ${Number(form.customization_min_price).toLocaleString()} EGP`
                      : 'Price: To be discussed'}
                  </ReviewValue>
                )}
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{ border: 0, background: 'none', color: '#764ba2', padding: '0.6rem 0 0', fontWeight: 700, cursor: 'pointer' }}
                >
                  Edit
                </button>
              </ReviewBox>
            )}

            <ReviewBox>
              <ReviewLabel>Catalog Items</ReviewLabel>
              <ReviewValue>{catalogItems.length} item{catalogItems.length !== 1 ? 's' : ''}</ReviewValue>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                {catalogItems.map((item, i) => (
                  <img key={i} src={item.previews[0]} alt={item.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '0.375rem' }} />
                ))}
              </div>
            </ReviewBox>

            <StatusNote>
              ⏳ Your shop will be reviewed by our team before going live. We'll notify you once it's approved or if changes are needed.
            </StatusNote>

            <BtnNext
              onClick={handleSubmit}
              disabled={submitting || finishingUploads}
              style={{ width: '100%', marginTop: '1.25rem', padding: '0.9rem', fontSize: '1rem' }}
            >
              {finishingUploads ? 'Finishing photo uploads…' : submitting ? 'Submitting…' : '🚀 Submit Shop for Review'}
            </BtnNext>
          </FormArea>
        )}

        {/* Navigation */}
        <NavRow>
          <BtnBack onClick={() => step === 0 ? navigate('/designers') : setStep(s => s - 1)} disabled={submitting}>
            {step === 0 ? '← Back' : '← Previous'}
          </BtnBack>
          {step < 3 ? (
            <BtnNext onClick={handleNext} disabled={finishingUploads}>
              {finishingUploads
                ? 'Finishing photo uploads…'
                : step === 2 && (catalogItems.length === 0 || catalogDraftStarted())
                  ? 'Save & Continue →'
                  : 'Next →'}
            </BtnNext>
          ) : (
            <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>↑ Click the button above</span>
          )}
        </NavRow>
      </Card>
    </Page>
  );
};

export default CreateShopPage;
